import React from "react";
import cvData from "../data/cvData";
import P5Backdrop from "../components/P5Backdrop";
import { Arrow, PageMeta } from "../components/SiteChrome";

const formatYears = (startDate, endDate) => {
  const startYear = startDate.split("/").pop();
  const endYear = endDate === "Present" ? "Now" : endDate.split("/").pop();
  return `${startYear}—${endYear}`;
};

export default function AboutPage() {
  return (
    <>
      <PageMeta
        title="Profile"
        description="Clément Girault is a Sydney-based digital strategist, service designer and multidisciplinary maker."
      />
      <section className="about-hero">
        <div className="about-portrait">
          <P5Backdrop variant="orbit" palette="red" />
          <img src="/images/avatar.png" alt="Clément Girault" />
          <span>Based in Sydney, Australia</span>
        </div>
        <div className="about-intro">
          <span className="eyebrow">Profile · Clément Girault</span>
          <h1>Clément<br />Girault</h1>
          <p>{cvData.summary}</p>
          <div className="about-actions">
            <a
              className="button button-dark"
              href="/cv"
              target="_blank"
              rel="noreferrer"
            >
              View full CV
              <span className="sr-only"> (opens in a new tab)</span>
              <Arrow />
            </a>
            <a className="text-link" href="mailto:clement.girault@icloud.com">
              Start a conversation <Arrow />
            </a>
          </div>
        </div>
      </section>

      <section className="profile-facts">
        {[
          ["25+", "Years across digital, media and technology"],
          [
            "4",
            "Languages",
            "English · French · Spanish · Hindi (beginner)",
          ],
          ["10+", "Organisations across public, private and not-for-profit sectors"],
          ["∞", "Curiosity across disciplines"],
        ].map(([value, label, detail]) => (
          <div key={label}>
            <strong>{value}</strong>
            <div className="profile-fact-copy">
              <span>{label}</span>
              {detail && <small>{detail}</small>}
            </div>
          </div>
        ))}
      </section>

      <section className="experience-section section-pad">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Experience</span>
            <h2>Strategy through<br />to delivery.</h2>
          </div>
          <p>
            Experience spanning government, education, cultural heritage and
            health organisations, alongside independent practice.
          </p>
        </div>
        <div className="experience-list">
          {cvData.experience.map((experience, index) => (
            <article key={`${experience.company}-${experience.startDate}`}>
              <span className="experience-number">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <span className="experience-date">
                  {experience.displayDate ||
                    formatYears(experience.startDate, experience.endDate)}
                </span>
                <h3>{experience.title}</h3>
                <h4>{experience.company}</h4>
              </div>
              <p>
                {experience.responsibilities
                  .slice(0, experience.profileResponsibilityCount || 2)
                  .join(" ")}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="capability-section">
        <div className="capability-title">
          <P5Backdrop variant="threads" palette="cobalt" />
          <span className="eyebrow">Capabilities</span>
          <h2>A problem-solver<br />at heart.</h2>
        </div>
        <div className="capability-list">
          {[
            ["Direction", "Digital strategy, project leadership, transformation, stakeholder engagement"],
            ["Experience", "Service design, UX research, information architecture, prototyping"],
            ["Technology", "AI, platform integration, workflow automation, APIs, accessibility"],
            ["Making", "React, Python, C++, Swift, photography, video and sound"],
          ].map(([title, body], index) => (
            <div key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="education-section section-pad">
        <div>
          <span className="eyebrow">Education</span>
          <h2>Formal foundations.</h2>
        </div>
        <div className="education-list">
          {cvData.education.map((education) => (
            <article key={`${education.degree}-${education.startDate}`}>
              <span>{formatYears(education.startDate, education.endDate)}</span>
              <div>
                <h3>{education.degree}</h3>
                <p>{education.institution}</p>
                {education.details.length > 0 && (
                  <ul className="education-details">
                    {education.details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
